import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded"
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded"
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded"
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded"

import { Box, Button, Card, CardContent, Grid, Tooltip, Typography } from "@mui/material"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { dateToString } from "../../helpers/date.helpers"
import { injectFontFaces } from "../../helpers/fontFace.helpers"
import { useOnScreen } from "../../hooks/useOnScreen"
import { useAppDispatch, useAppSelector } from "../../hooks/useRedux"
import { getFileAsync } from "../../store/slices/file.slice"
import {
    duplicateGraphicAsync,
    exportGraphicAsync,
    getGraphicAsync,
    setFinalGraphicAsync,
} from "../../store/slices/graphic.slice"

import { Collections, ElectricalServices, Image } from "@mui/icons-material"
import { useTheme } from "@mui/material/styles"
import { FaFont } from "react-icons/fa"

import { EMPTY_IMAGE_SRC } from "tweenly"
import { getFullNameString } from "../../helpers/string.helpers"
import useHTMLFileContent from "../../hooks/useHTMLFileContent"
import { useToast } from "../../hooks/useToast"
import { useVerifyPermission } from "../../hooks/useVerifyPermission"
import { getCompanyStatsAsync } from "../../store/slices/companyStats.slice"
import {
    GraphicAsset,
    getGraphicAfterIsFinal,
    getGraphicsAfterExport,
} from "../../store/slices/graphics.slice"
import { getThumbnailFileAsync } from "../../store/slices/thumbnailMap.slice"
import SkeletonGraphicTile from "../GraphicCommon/SkeletonGraphicTile"
import UnPublishDialog from "../GraphicCommon/UnPublishDialog"
import AlertDialog from "../common/AlertDialog"
import ButtonWithTooltip from "../common/ButtonWithTooltip"
import MoreContextMenu, { Item } from "../common/ContextMenu/MoreContextMenu"
import LoadingBackdrop from "../common/LoadingBackdrop"
import GraphicPublishDetail from "./GraphicPublishDetail"

export const getIcon = (type: string) => {
    switch (type) {
        case "font":
            return <FaFont />
        case "json":
        case "xml":
        case "text":
            return <ElectricalServices />
        case "image":
            return <Image />
        case "sequence":
            return <Collections />
        default:
            return
    }
}

export const getGraphicTooltip = (type?: string) => {
    switch (type) {
        case "font":
            return "font"
        case "json":
        case "xml":
        case "text":
            return "dataSource"
        case "image":
            return "image"
        case "sequence":
            return "sequence"
        default:
            return "graphic"
    }
}

interface Props {
    graph: GraphicAsset
    onDelGraphic: (graphic: GraphicAsset) => void
    handleOpenDetail: (graphic: GraphicAsset) => void
    setFirstPage: () => void
}

function Graphic({ graph, onDelGraphic, handleOpenDetail, setFirstPage }: Props) {
    const theme = useTheme()
    const { t } = useTranslation()
    const user = useAppSelector((state) => state.user.value)
    const { canDuplicate, canDelete, canExport, canExportPackage, isFreeLicense } =
        useVerifyPermission(graph, graph.company)
    const dispatch = useAppDispatch()
    const { success } = useToast()

    const [openPublishDetail, setOpenPublishDetail] = useState(false)
    const [openUnPublishDetail, setOpenUnPublishDetail] = useState(false)
    const [notFinalYet, setNotFinalYet] = useState<boolean>(false)
    const [openDelAlert, setOpenDelAlert] = useState(false)
    const [openLockedDeleteDialog, setOpenLockedDeleteDialog] = useState(false)
    const ref: any = useRef<HTMLDivElement>()
    const onScreen: boolean = useOnScreen<HTMLDivElement>(ref, "-300px")
    const thumbnailMap = useAppSelector((state) => state.thumbnailMap)
    const tooltipPermission = graph.userId !== user._id ? t("graphics:noPermission") : undefined

    const fileContent = useAppSelector((state) => state.file.data)

    const thumbnailSrc = useMemo(() => {
        if (!graph.type) {
            return graph.url ?? thumbnailMap.data[graph.thumbnailId!]?.src
        } else if (["image", "sequence"].includes(graph.type)) {
            return graph.url
        } else {
            return undefined
        }
    }, [graph, thumbnailMap.data])

    useEffect(() => {
        if (graph.thumbnailId && thumbnailSrc === undefined) {
            dispatch(getThumbnailFileAsync(graph.thumbnailId))
        }
    }, [dispatch, graph.thumbnailId, onScreen, thumbnailSrc])

    const prepareDetailData = async () => {
        await dispatch(getGraphicAsync(graph._id))
        if (graph.fileId) await dispatch(getFileAsync(graph.fileId))
    }

    const handleFinishAndPublish = async () => {
        dispatch(setFinalGraphicAsync(graph, { isFinal: true }))
        dispatch(
            getGraphicAfterIsFinal({
                id: graph._id,
                isFinal: true,
            })
        )

        await prepareDetailData()

        setNotFinalYet(false)
        setOpenPublishDetail(true)
    }
    const handleClosePublishDetail = async () => {
        setOpenPublishDetail(false)
    }

    const handleCloseUnPublishDetail = () => {
        setOpenUnPublishDetail(false)
    }

    const handleOpenDelAlert = () => {
        if (graph.isFinal && !graph.type) {
            setOpenLockedDeleteDialog(true)
            return
        }
        setOpenDelAlert(true)
    }
    const onDelete = () => {
        setOpenDelAlert(false)
        onDelGraphic(graph)
    }

    const handleDuplicate = async () => {
        await dispatch(duplicateGraphicAsync(graph._id))
        setFirstPage()
    }

    const handleAfterExport = () => {
        dispatch(getCompanyStatsAsync(graph.companyId, true))
        dispatch(getGraphicsAfterExport({ id: graph._id }))
    }

    const { downloadFile, downloadZip } = useHTMLFileContent()

    const handleExportHtml = async () => {
        if (!graph.isExported) {
            dispatch(exportGraphicAsync(graph._id, undefined, handleAfterExport))
        }
        await downloadFile(graph.fileId!, graph.name)
        success("graphics:successMessage.graphicExported")
    }

    const handleExportZip = async () => {
        if (!graph.isExported) {
            dispatch(exportGraphicAsync(graph._id, undefined, handleAfterExport))
        }
        await downloadZip(graph.fileId!, graph.name)
        success("graphics:successMessage.graphicExported")
    }

    const menu: Item[] = [
        {
            icon: <DeleteRoundedIcon />,
            title: t("graphics:delete"),
            onClick: handleOpenDelAlert,
            disabled: Boolean(graph.type) && !canDelete,
            tooltipTitle: tooltipPermission,
        },
    ]

    if (!graph.type) {
        menu.unshift(
            ...[
                {
                    icon: <ContentCopyRoundedIcon />,
                    title: t("graphics:duplicate"),
                    onClick: handleDuplicate,
                    divider: true,
                    disabled: !canDuplicate,
                },
                {
                    icon: <FileDownloadRoundedIcon />,
                    title: t("graphics:exportZip"),
                    onClick: handleExportZip,
                    disabled: !canExportPackage,
                    tooltipTitle: !canExportPackage ? t("menu:noExportPackagePermission") : "",
                },
                {
                    icon: <FileDownloadRoundedIcon />,
                    title: t("graphics:exportHtml"),
                    onClick: handleExportHtml,
                    disabled: !canExport,
                    divider: true,
                    tooltipTitle: isFreeLicense ? t("menu:exportHtmlInfo") : "",
                    infoIcon: isFreeLicense ? true : false,
                },
            ]
        )
    }

    useEffect(() => {
        if (graph.type === "font") {
            injectFontFaces([{ name: graph.name, url: graph.url }])
        }
    }, [graph])

    return (
        <>
            <Card
                ref={ref}
                sx={{
                    width: 350,
                    backgroundColor: theme.palette.background.paper,
                    padding: 1,
                    borderRadius: 1,
                    minHeight: 100,
                    margin: 1.25,
                    position: "relative",
                }}
            >
                {graph.type && (
                    <>
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                right: 0,
                                borderTop: `60px solid ${theme.palette.grey[500]}`,
                                borderLeft: "60px solid transparent",
                            }}
                        ></Box>
                        <Box
                            sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                            }}
                        >
                            {getIcon(graph.type)}
                        </Box>
                    </>
                )}

                <CardContent>
                    {!thumbnailSrc && graph?.thumbnailId ? (
                        <SkeletonGraphicTile />
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                backgroundColor: theme.palette.grey[900],
                                height: 170,
                            }}
                        >
                            {graph.type === "font" ? (
                                <Box sx={{ fontFamily: graph.name }}>{graph.name}</Box>
                            ) : graph.type && ["json", "xml", "text"].includes(graph.type) ? (
                                <Box sx={{ fontFamily: "code" }}>{`${t(
                                    "graphics:dataSourceTile"
                                )} (${graph.type})`}</Box>
                            ) : (
                                <Box
                                    component="img"
                                    src={thumbnailSrc ?? EMPTY_IMAGE_SRC}
                                    sx={{
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                        opacity: 0.7,
                                        p: 0,
                                    }}
                                />
                            )}
                        </Box>
                    )}
                    <Grid container mt={1}>
                        <Grid item xs={11}>
                            <Typography variant="h6" sx={{ wordBreak: "break-word" }}>
                                {graph.name}
                            </Typography>
                            <Typography variant="body2">{graph.company?.name}</Typography>
                            <Typography variant="body2">
                                {graph.updated && dateToString(graph.updated)}
                                {" | "}
                                <i>
                                    {!graph.user?.firstName && !graph.user?.lastName
                                        ? graph.user?.email
                                        : ""}
                                </i>
                                {getFullNameString(graph.user?.firstName, graph.user?.lastName)}
                            </Typography>
                            <Typography variant="body2">
                                {graph.size && `${Math.round(graph.size / 1000)} KB`}
                            </Typography>
                        </Grid>
                        <Grid item xs={1}>
                            <MoreContextMenu placement="left" data={menu} />
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        mt={1}
                        flexDirection="row"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Grid item>
                            <Grid container>
                                {graph.isFinal && (
                                    <Grid item pr={1}>
                                        <Tooltip title={t("graphics:isFinal")}>
                                            <CheckCircleRoundedIcon />
                                        </Tooltip>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>

                        <Grid item>
                            <ButtonWithTooltip
                                tooltipTitle={t(
                                    `graphics:openDetailButtonTooltip.${getGraphicTooltip(
                                        graph.type
                                    )}`
                                )}
                                buttonComponent={
                                    <Button
                                        aria-label="menu"
                                        size="large"
                                        variant="text"
                                        onClick={() => handleOpenDetail(graph)}
                                    >
                                        {t("graphics:openDetailButton")}
                                    </Button>
                                }
                                disabled={false}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            {openPublishDetail && (
                <GraphicPublishDetail
                    open={openPublishDetail}
                    fileContent={fileContent?.blob}
                    onClose={() => handleClosePublishDetail()}
                    setFirstPage={() => setFirstPage()}
                />
            )}
            <UnPublishDialog
                open={openUnPublishDetail}
                onClose={() => handleCloseUnPublishDetail()}
                setFirstPage={() => setFirstPage()}
            />
            <AlertDialog
                open={openDelAlert && !graph.isFinal}
                title={`${t("graphics:delete")} '${graph.name}' ?`}
                content={t("graphics:deleteQuestion")}
                onDisagree={() => setOpenDelAlert(false)}
                onAgree={() => onDelete()}
                txtDisagree={t("disagree")}
                txtAgree={t("delete")}
                colorAgree="error"
            />
            <AlertDialog
                open={openLockedDeleteDialog}
                title={t("graphics:lockedDialogs.delete.title")}
                content={t("graphics:lockedDialogs.delete.content")}
                txtAgree={t("agree")}
                onAgree={() => setOpenLockedDeleteDialog(false)}
            />
            <AlertDialog
                open={notFinalYet}
                title={t("graphics:finishDialog.title")}
                content={t("graphics:finishDialog.content")}
                onDisagree={() => setNotFinalYet(false)}
                onAgree={handleFinishAndPublish}
                txtDisagree={t("graphics:finishDialog.disagree")}
                txtAgree={t("graphics:finishDialog.agree")}
                colorAgree="primary"
            />
            <LoadingBackdrop open={!fileContent && openPublishDetail} />
        </>
    )
}

export default Graphic
